import React from 'react';
import { Plus, Trash, Copy, Clipboard } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { SimpleDropdown } from '@/components/ui/simple-dropdown';
import { CardContent } from '@/components/ui/card';
import {
  DueDiligenceAnswerType,
  DueDiligenceStatusColor,
  statusColorToClasses,
  ConditionGroup,
} from 'shared/types/due-diligence';
import { AnswerOptionConditionBuilder } from 'components/dueDiligence/AnswerOptionConditionBuilder';
import type { QuestionItem, AnswerOptionItem } from './types';
import { STATUS_COLOR_OPTIONS } from './types';
import type { DatabaseFileLabel } from 'shared/components/UniversalPickerModal';

interface AnswerOptionsSectionProps {
  question: QuestionItem;
  questionIndex: number;
  questions: QuestionItem[];
  fileLabels: DatabaseFileLabel[];
  conditionsClipboard: ConditionGroup | null;
  onAddOption: (questionIndex: number) => void;
  onUpdateOption: (questionIndex: number, optionIndex: number, updates: Partial<AnswerOptionItem>) => void;
  onRemoveOption: (questionIndex: number, optionIndex: number) => void;
  onCopyConditions: (questionIndex: number, optionIndex: number) => void;
  onPasteConditions: (questionIndex: number, optionIndex: number) => void;
}

const statusColorDropdownOptions = STATUS_COLOR_OPTIONS.map((opt) => ({
  value: opt.value,
  label: (
    <div className="flex items-center gap-2">
      <span className={`w-3 h-3 rounded-full ${opt.color}`} />
      {opt.label}
    </div>
  ),
}));

export const AnswerOptionsSection: React.FC<AnswerOptionsSectionProps> = ({
  question,
  questionIndex,
  questions,
  fileLabels,
  conditionsClipboard,
  onAddOption,
  onUpdateOption,
  onRemoveOption,
  onCopyConditions,
  onPasteConditions,
}) => {
  const isSimpleSelect = question.answerType === DueDiligenceAnswerType.SIMPLE_SELECT;

  return (
    <CardContent className="pt-4 border-t" data-tour="dd-answer-options">
      <div className="flex items-center justify-between mb-3">
        <div>
          <span className="text-sm font-medium">Antwoordopties</span>
          <p className="text-xs text-muted-foreground">
            {isSimpleSelect
              ? 'Definieer de mogelijke antwoorden (geen status bolletje in overzicht)'
              : 'Definieer de mogelijke antwoorden en hun status kleuren'}
          </p>
        </div>
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => onAddOption(questionIndex)}>
          <Plus className="h-3 w-3 mr-1" />
          Optie toevoegen
        </Button>
      </div>

      {question.answerOptions.length === 0 ? (
        <div className="text-center py-6 text-xs text-muted-foreground border border-dashed rounded-lg bg-muted/20">
          <p className="font-medium mb-1">Nog geen antwoordopties</p>
          <p>Voeg opties toe zoals &quot;Ja&quot;, &quot;Nee&quot;, &quot;N.v.t.&quot; met hun status kleuren</p>
        </div>
      ) : (
        <div className="space-y-3">
          {question.answerOptions.map((option, optIndex) => (
            <div key={option.id || `opt-${optIndex}`} className="p-3 bg-muted/30 rounded-lg border border-muted">
              <div className="flex items-center gap-3">
                {!isSimpleSelect && (
                  <div
                    className={`w-5 h-5 rounded-full shrink-0 ${statusColorToClasses[option.statusColor]} ring-2 ring-white shadow-sm`}
                  />
                )}

                <div className={`flex-1 grid gap-3 ${isSimpleSelect ? 'grid-cols-1' : 'grid-cols-4'}`}>
                  <Input
                    placeholder="Label (bijv. Ja)"
                    value={option.label}
                    onChange={(e) =>
                      onUpdateOption(questionIndex, optIndex, {
                        label: e.target.value,
                        value: e.target.value
                          .toLowerCase()
                          .replace(/\s+/g, '_')
                          .replace(/[^a-z0-9_]/g, ''),
                      })
                    }
                    className="h-9 text-sm"
                  />
                  {!isSimpleSelect && (
                    <>
                      <SimpleDropdown
                        value={option.statusColor}
                        onChange={(value) =>
                          onUpdateOption(questionIndex, optIndex, {
                            statusColor: value as DueDiligenceStatusColor,
                          })
                        }
                        options={statusColorDropdownOptions}
                        triggerClassName="h-9 text-sm"
                      />
                      <Input
                        placeholder="Tooltip tekst"
                        value={option.tooltipLabel || ''}
                        onChange={(e) => onUpdateOption(questionIndex, optIndex, { tooltipLabel: e.target.value })}
                        className="h-9 text-sm"
                      />
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={option.isDefault}
                          onCheckedChange={(checked) => onUpdateOption(questionIndex, optIndex, { isDefault: checked })}
                        />
                        <span className="text-xs">Standaard</span>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-0.5">
                  {question.documentLabelIds.length > 0 && option.conditionRoot && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-blue-600"
                      onClick={() => onCopyConditions(questionIndex, optIndex)}
                      title="Kopieer voorwaarden"
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  )}
                  {question.documentLabelIds.length > 0 && conditionsClipboard && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-green-600"
                      onClick={() => onPasteConditions(questionIndex, optIndex)}
                      title="Plak voorwaarden"
                    >
                      <Clipboard className="h-3 w-3" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-destructive hover:text-destructive"
                    onClick={() => onRemoveOption(questionIndex, optIndex)}
                    title="Verwijder optie"
                  >
                    <Trash className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {/* Condition builder */}
              {(question.documentLabelIds.length > 0 ||
                questions.filter((q) => q.id !== question.id).length > 0) && (
                <AnswerOptionConditionBuilder
                  conditionRoot={option.conditionRoot}
                  onChange={(conditionRoot) => onUpdateOption(questionIndex, optIndex, { conditionRoot })}
                  linkedLabels={fileLabels
                    .filter((fl) => question.documentLabelIds.some((id) => String(id) === String(fl.id)))
                    .map((fl) => ({ id: fl.id, name: fl.label }))}
                  extractionQuestions={questions
                    .filter(
                      (q) =>
                        q.id !== question.id &&
                        q.answerType === DueDiligenceAnswerType.EXTRACTION,
                    )
                    .map((q) => ({
                      fieldName: q.fieldName,
                      displayName: q.displayName,
                      extractionLabel: q.extractionLabel,
                      extractionValueType: q.extractionValueType,
                    }))}
                  availableQuestions={questions
                    .filter((q) => q.id != null && q.id !== question.id)
                    .map((q) => ({
                      id: q.id!,
                      displayName: q.displayName,
                      fieldName: q.fieldName,
                      answerOptions: q.answerOptions
                        ?.filter((opt) => opt.active)
                        .map((opt) => ({
                          id: opt.id,
                          label: opt.label,
                          value: opt.value,
                          statusColor: opt.statusColor,
                          active: opt.active,
                        })),
                    }))}
                  currentQuestionId={question.id}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </CardContent>
  );
};
